import { useCallback, useEffect, useState } from 'react';
import { NoteService } from '../../../../services/noteService';
import { ProfileCommentService } from '../../../../services/profileCommentService';
import { MemoryService } from '../../../../services/memoryService';
import type { Note, Comment, Memory } from '../../../../types';

export type ActivityType = 'note' | 'comment' | 'memory' | 'all';

export interface ActivityItem {
  id: string;
  type: 'note' | 'comment' | 'memory';
  createdAt: Date;
  updatedAt?: Date;
  authorId: string;
  authorName: string;
  content: string;
  data: Note | Comment | Memory;  // Original item data
}

interface UseUnifiedActivityResult {
  items: ActivityItem[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

/**
 * useUnifiedActivity - Hook for fetching and managing unified activity stream
 *
 * Combines notes, comments, and memories into a single chronological feed
 */
export const useUnifiedActivity = (
  personId: string,
  filter: ActivityType,
  userId: string
): UseUnifiedActivityResult => {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  /**
   * Load activity items based on filter
   */
  const loadActivity = useCallback(async (pageNum: number = 1) => {
    if (pageNum === 1) {
      setLoading(true);
    }
    setError(null);

    try {
      const promises: Promise<any[]>[] = [];

      // Fetch based on filter
      if (filter === 'all' || filter === 'note') {
        promises.push(NoteService.getNotesByPerson(personId, {}, userId));
      }
      if (filter === 'all' || filter === 'comment') {
        promises.push(ProfileCommentService.getCommentsByPerson(personId));
      }
      if (filter === 'all' || filter === 'memory') {
        promises.push(MemoryService.getMemoriesByPerson(personId, {}, userId));
      }

      const results = await Promise.all(promises);

      // Normalize all items into ActivityItem format
      const normalized: ActivityItem[] = [];

      let resultIndex = 0;

      if (filter === 'all' || filter === 'note') {
        const notes = results[resultIndex] || [];
        notes.forEach((note: Note) => {
          if (!note.isDeleted) {
            normalized.push({
              id: note.id,
              type: 'note',
              createdAt: note.createdAt,
              updatedAt: note.updatedAt,
              authorId: note.authorId,
              authorName: note.authorName,
              content: note.content,
              data: note
            });
          }
        });
        resultIndex++;
      }

      if (filter === 'all' || filter === 'comment') {
        const comments = results[resultIndex] || [];
        comments.forEach((comment: Comment) => {
          if (!comment.isDeleted) {
            normalized.push({
              id: comment.id,
              type: 'comment',
              createdAt: comment.createdAt,
              updatedAt: comment.updatedAt,
              authorId: comment.authorId,
              authorName: comment.authorName,
              content: comment.content,
              data: comment
            });
          }
        });
        resultIndex++;
      }

      if (filter === 'all' || filter === 'memory') {
        const memories = results[resultIndex] || [];
        memories.forEach((memory: Memory) => {
          if (!memory.isDeleted) {
            normalized.push({
              id: memory.id,
              type: 'memory',
              createdAt: memory.createdAt,
              updatedAt: memory.updatedAt,
              authorId: memory.authorId,
              authorName: memory.authorName,
              content: memory.content,
              data: memory
            });
          }
        });
      }

      // Sort by createdAt descending (newest first)
      normalized.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      // Pagination
      const startIndex = (pageNum - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const paginatedItems = normalized.slice(startIndex, endIndex);

      if (pageNum === 1) {
        setItems(paginatedItems);
      } else {
        setItems(prev => [...prev, ...paginatedItems]);
      }

      setHasMore(normalized.length > endIndex);
      setPage(pageNum);

    } catch (err: any) {
      setError(err.message || 'Failed to load activity');
      console.error('Error loading activity:', err);
    } finally {
      setLoading(false);
    }
  }, [personId, filter, userId]);

  /**
   * Load more items (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await loadActivity(page + 1);
  }, [hasMore, loading, page, loadActivity]);

  /**
   * Refresh feed (reset to page 1)
   */
  const refresh = useCallback(async () => {
    setPage(1);
    await loadActivity(1);
  }, [loadActivity]);

  // Initial load
  useEffect(() => {
    loadActivity(1);
  }, [loadActivity]);

  return { items, loading, error, refresh, hasMore, loadMore };
};
